const API_BASE = '/api/compliance/gap-assessments';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface GapAssessment {
  id: number;
  company_id: number;
  framework_id: number;
  framework_name?: string;
  title: string;
  status: 'draft' | 'in_progress' | 'completed';
  overall_score?: number;
  conducted_by: number;
  conducted_by_name?: string;
  item_count?: number;
  created_at: string;
}

export interface GapAssessmentItem {
  id: number;
  assessment_id: number;
  compliance_page_id: number;
  requirement_ref?: string;
  current_state?: string;
  risk_level: 'met' | 'partial' | 'not_met';
  remediation_notes?: string;
  estimated_effort?: number;
}

export const gapAssessmentApi = {
  async list(params?: { page?: number; limit?: number; status?: string; company_id?: number }): Promise<{ assessments: GapAssessment[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    if (params?.company_id) q.set('company_id', String(params.company_id));
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async create(data: { company_id: number; framework_id: number; title: string }): Promise<GapAssessment> {
    const res = await fetch(API_BASE, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async get(id: number): Promise<GapAssessment & { items: GapAssessmentItem[] }> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateItem(assessmentId: number, itemId: number, data: Partial<GapAssessmentItem>): Promise<GapAssessmentItem> {
    const res = await fetch(`${API_BASE}/${assessmentId}/items/${itemId}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async delete(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async complete(id: number): Promise<GapAssessment> {
    const res = await fetch(`${API_BASE}/${id}/complete`, { method: 'PUT', headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getRemediationPlan(id: number): Promise<GapAssessmentItem[]> {
    const res = await fetch(`${API_BASE}/${id}/remediation-plan`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getStats(companyId?: number): Promise<{ total: number; draft: number; in_progress: number; completed: number; avg_score: number }> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/stats${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
